import { LoaderFunctionArgs, useParams } from 'react-router-dom';

import { Center, Flex, Spinner } from '@chakra-ui/react';

import EmployeeProfileDetails from 'app/components/ManagePeople/GiftingEmployeeProfile/EmployeeProfileDetails';
import EmployeeProfileGiftingDetails from 'app/components/ManagePeople/GiftingEmployeeProfile/EmployeeProfileGiftingDetails';
import EmployeeProfileHeader from 'app/components/ManagePeople/GiftingEmployeeProfile/EmployeeProfileHeader';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import { EmptyState } from '@aslanpay/portal-common-lib';

import useFetchIndividual, { fetchIndividualQuery } from 'app/hooks/useFetchIndividual';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

export const giftingEmployeeProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchIndividualQuery(params.employeeId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function GiftingEmployeeProfile() {
  const { employeeId } = useParams();
  const queryClient = useQueryClient();

  const { data: employee, isInitialLoading: isLoadingEmployee } = useFetchIndividual(employeeId);

  const handleInvalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchIndividual', employeeId] });
    queryClient.refetchQueries({ queryKey: ['fetchIndividual', employeeId] });
  };

  if (isLoadingEmployee) {
    return (
      <Center width="full" height="full" flexGrow={1}>
        <Spinner color="brand.purple" size="xl" mb="50px" />
      </Center>
    );
  }

  if (!employee) {
    return (
      <AuthenticatedPageWrapper title="Employee Not Found">
        <EmptyState text="The requested employee was not found." />
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper title={employee.firstName || 'Profile'}>
      <Flex flexDir="column" gap="30px" flexGrow={1}>
        <EmployeeProfileHeader employee={employee} onInvalidateQuery={handleInvalidateQuery} />

        <Flex flexDir="column" gap="40px">
          <EmployeeProfileDetails employee={employee} onInvalidateQuery={handleInvalidateQuery} />
          <EmployeeProfileGiftingDetails employee={employee} />
        </Flex>
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default GiftingEmployeeProfile;
